import { Pencil } from 'lucide-react';

import { Botao } from '@/componentes/Botao';
import { Medida } from '@/componentes/Medida';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { formatarDataHora, formatarReais } from '@/lib/formato';

import { litrosEscritos } from './litros';
import {
  fichaDoAbastecimento,
  maquinaPorExtenso,
  quemLancou,
  situacaoDoAbastecimento,
  type EstadoDoAbastecimento,
  type ItemDaFicha,
} from './registro';
import { totalDoAbastecimento, type Abastecimento } from './tipos';

/**
 * O PAINEL DO ABASTECIMENTO da lista principal (p.28) — pedido do dono,
 * 27/10/2026: "mais detalhes para ver quem lançou".
 *
 * É o painel da tela de lançamento com outra pergunta. Lá a pergunta é "este
 * número está certo?", e o painel abre nas verificações. Aqui é "o que é este
 * lançamento e quem o fez?", e ele abre no lançamento inteiro.
 *
 * TRÊS ABAS: o lançamento (o fato), o registro (quem, quando, por onde, quem
 * estava na máquina e quem liberou) e a situação (vale, estornado, corrigido).
 * O conteúdo das duas primeiras é `fichaDoAbastecimento`, a mesma função que a
 * folha do celular lê — os dois lados dizem a mesma coisa.
 *
 * SEM AÇÕES no painel: corrigir, estornar e apagar moram nos três pontos da
 * linha, que ficam à vista ao lado do painel fixo. Uma janela aberta de dentro
 * da gaveta seria a sobreposição empilhada que a p.29 proíbe.
 */
export function DetalheDoAbastecimento({
  abastecimento: a,
  porId,
  aoAbrirOutro,
  aoFechar,
}: {
  readonly abastecimento: Abastecimento;
  /** A lista inteira, vigentes e estornados: o outro lado da correção mora nela. */
  readonly porId: ReadonlyMap<string, Abastecimento>;
  readonly aoAbrirOutro: (id: string) => void;
  readonly aoFechar: () => void;
}) {
  const ficha = fichaDoAbastecimento(a);
  const situacao = situacaoDoAbastecimento(a, porId);
  const total = totalDoAbastecimento(a);
  const estornado = Boolean(a.estorno);

  return (
    <PainelDetalhe
      codigo={a.equipamentoCodigo}
      titulo={maquinaPorExtenso(a.equipamentoDescricao, a.equipamentoTipo) ?? 'Sem descrição cadastrada'}
      subtitulo={`${a.produto ?? 'Sem produto'} · ${formatarDataHora(a.quando)}`}
      marcadores={
        estornado ? (
          <SeloEstornado />
        ) : situacao.estado === 'correcao' ? (
          <EtiquetaCorrigido />
        ) : undefined
      }
      /* QUATRO dados-chave, os que respondem a pergunta do dono: quanto, por
         quanto, quem lançou e quando aconteceu. */
      dadosChave={
        <>
          <DadoChave rotulo="Litros">
            {estornado ? (
              <span className="numero text-tinta-apagada line-through">{`${litrosEscritos(a.litros)} L`}</span>
            ) : (
              <Medida valor={litrosEscritos(a.litros)} unidade="L" />
            )}
          </DadoChave>
          <DadoChave rotulo="Total">
            {total === null ? <span className="text-tinta-secundaria">Sem preço</span> : <span className={estornado ? 'numero text-tinta-apagada line-through' : 'numero'}>{formatarReais(total)}</span>}
          </DadoChave>
          <DadoChave rotulo="Lançado por">
            <span className={a.registro?.por ? '' : 'text-tinta-secundaria'}>{quemLancou(a)}</span>
          </DadoChave>
          <DadoChave rotulo="Ocorreu em">{formatarDataHora(a.quando)}</DadoChave>
        </>
      }
      abas={[
        { id: 'lancamento', rotulo: 'Lançamento', conteudo: <ListaDaFicha itens={ficha.lancamento} /> },
        { id: 'registro', rotulo: 'Registro', conteudo: <ListaDaFicha itens={ficha.registro} /> },
        {
          id: 'situacao',
          rotulo: 'Situação',
          conteudo: (
            <div className="flex flex-col gap-3">
              <DadoChave rotulo="Situação">{TITULO_DA_SITUACAO[situacao.estado]}</DadoChave>
              <p className="txt-corpo text-pretty text-tinta-secundaria">{situacao.texto}</p>
              {situacao.relacionado ? (
                <div>
                  <Botao
                    nivel="secundario"
                    onClick={() => {
                      if (situacao.relacionado) aoAbrirOutro(situacao.relacionado.id);
                    }}
                  >
                    {situacao.relacionado.rotulo}
                  </Botao>
                </div>
              ) : null}
            </div>
          ),
        },
      ]}
      aoFechar={aoFechar}
    />
  );
}

const TITULO_DA_SITUACAO: Readonly<Record<EstadoDoAbastecimento, string>> = {
  vale: 'Vale',
  estornado: 'Estornado',
  corrigido: 'Estornado e corrigido',
  correcao: 'Correção de um lançamento estornado',
};

/**
 * Os itens da ficha no formato do dado-chave, na grade de dados do painel:
 * duas colunas no painel estreito, quatro na janela larga em que ele abre
 * (pedido do dono, 31/10/2026).
 */
function ListaDaFicha({ itens }: { readonly itens: readonly ItemDaFicha[] }) {
  return (
    <div className="grade-de-dados">
      {itens.map((item) => (
        <DadoChave key={item.rotulo} rotulo={item.rotulo}>
          {typeof item.valor === 'string' ? (
            <span className={item.ausente ? 'font-normal text-tinta-secundaria' : ''}>{item.valor}</span>
          ) : (
            <Medida valor={item.valor.valor} unidade={item.valor.unidade} {...(item.valor.colado ? { colado: true } : {})} />
          )}
        </DadoChave>
      ))}
    </div>
  );
}

/**
 * O SELO DO ESTORNADO (R02, p.32): preenchido, com ponto e o nome escrito —
 * o mesmo da coluna Verificação da tela de lançamento, no neutro: o estornado
 * não é erro nem alerta, é um lançamento que deixou de valer.
 */
export function SeloEstornado({ titulo }: { readonly titulo?: string | undefined }) {
  return (
    <span
      {...(titulo ? { title: titulo } : {})}
      className="inline-flex h-[var(--selo-altura)] items-center gap-1.5 rounded-pill bg-nao-alocado-fundo px-2 text-nao-alocado-texto"
    >
      <span aria-hidden="true" className="size-[var(--selo-ponto)] rounded-pill bg-nao-alocado" />
      <span className="txt-selo">Estornado</span>
    </span>
  );
}

/** A ETIQUETA DO CORRIGIDO (R06, p.69): âmbar, com o lápis de Editar, e o motivo no `title`. */
export function EtiquetaCorrigido({ motivo }: { readonly motivo?: string | null | undefined }) {
  const texto = motivo === undefined ? null : `Motivo: ${motivo ?? 'não registrado'}`;
  return (
    <span
      {...(texto ? { title: texto } : {})}
      /* `relative`: o `sr-only` abaixo é absoluto, e sem pai posicionado ele
         escapa do rolador e estica o documento (o mesmo defeito da etiqueta
         na tela de lançamento). */
      className="relative inline-flex h-6 items-center gap-1 rounded-etiqueta bg-atencao-fundo px-2 txt-legenda text-atencao-texto"
    >
      <Pencil aria-hidden="true" className="size-3 shrink-0" />
      Corrigido
      {texto ? <span className="sr-only">{`. ${texto}`}</span> : null}
    </span>
  );
}
